/*
 * TMG Operations Wheel, top navigation bar. Brand, a settings menu, who is
 * signed in, and logout. The settings menu emits a window event that the wheel
 * view listens for (reset weightings / wipe everything).
 */
import React, {Component} from 'react';
import {Navbar, Nav} from 'react-bootstrap';

const APP_NAME = 'TMG Operations Wheel';
// Build version, stamped in by webpack DefinePlugin (git sha + build time). Lets
// you confirm in the running app which deploy you are actually looking at.
// eslint-disable-next-line no-undef
const APP_VERSION = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : 'dev';

function signedInName(userPool) {
  try {
    const token = localStorage.getItem('idToken');
    if (token) {
      let b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      b64 += '='.repeat((4 - (b64.length % 4)) % 4);
      const p = JSON.parse(atob(b64));
      return p.email || p.name || p['cognito:username'];
    }
  } catch (e) { /* fall through */ }
  const user = userPool && userPool.getCurrentUser();
  return user ? user.getUsername() : '';
}

function emitSettings(action) {
  try { window.dispatchEvent(new CustomEvent('tmg-settings', {detail: {action}})); } catch (e) { /* ignore */ }
}

function emitTakeControl() {
  try { window.dispatchEvent(new CustomEvent('tmg-take-control')); } catch (e) { /* ignore */ }
}

class Navigation extends Component {
  // driver: {mode: 'none'|'me'|'other', driverName?, canTake?} broadcast by the wheel.
  state = {menuOpen: false, driver: {mode: 'none'}};
  toggle = () => this.setState((s) => ({menuOpen: !s.menuOpen}));
  close = () => this.setState({menuOpen: false});
  choose = (action) => { this.close(); emitSettings(action); };

  // The wheel view broadcasts driver status; reflect it in the top bar so every
  // signed-in watcher sees who is driving and can take control.
  componentDidMount() {
    this._onDriving = (e) => this.setState({driver: (e.detail && e.detail.mode) ? e.detail : {mode: 'none'}});
    window.addEventListener('tmg-driving', this._onDriving);
  }
  componentWillUnmount() {
    window.removeEventListener('tmg-driving', this._onDriving);
  }

  render() {
    const who = signedInName(this.props.userPool);
    const {menuOpen, driver} = this.state;
    const mode = (driver && driver.mode) || 'none';
    return (
      <Navbar expand="lg" className="px-3" style={{backgroundColor: '#EFEFEF', minHeight: '56px'}}>
        <Nav style={{height: '100%'}}>
          <Navbar.Brand className="me-3" style={{fontWeight: 800}}>{APP_NAME}</Navbar.Brand>
        </Nav>
        <Nav className="ms-auto" style={{alignItems: 'center'}}>
          {mode === 'other' ? (
            <>
              <Navbar.Text className="me-2"><strong>{driver.driverName}</strong> is driving</Navbar.Text>
              <button style={{...S.take, ...(driver.canTake ? {} : S.takeOff)}} className="me-3"
                onClick={emitTakeControl} disabled={!driver.canTake}
                title={driver.canTake ? 'Take control of the wheel' : "Can't take control mid-spin"}>
                Take control
              </button>
            </>
          ) : (
            <Navbar.Text className="me-3">{mode === 'me' ? 'Driving: ' : 'Signed in as '}<strong>{who}</strong></Navbar.Text>
          )}
          <div style={{position: 'relative'}}>
            <button onClick={this.toggle} style={S.gear} title="Settings">&#9881; Settings</button>
            {menuOpen && (
              <>
                <div style={S.overlay} onClick={this.close} />
                <div style={S.menu}>
                  <button style={S.item} onClick={() => this.choose('newRound')}>
                    New round
                    <span style={S.sub}>Everyone present, temporary guests removed (leadership only)</span>
                  </button>
                  <button style={S.item} onClick={() => this.choose('resetWeightings')}>
                    Reset weightings
                    <span style={S.sub}>Even the odds again — keeps the spoken counts</span>
                  </button>
                  <button style={S.item} onClick={() => this.choose('wipeAll')}>
                    Wipe everything
                    <span style={S.sub}>Clear all counts and start a fresh round</span>
                  </button>
                  {driver.isLeader && (
                    <button style={S.item} onClick={() => this.choose('testMode')}>
                      {driver.testMode ? 'Exit test mode' : 'Test mode'}
                      <span style={S.sub}>
                        {driver.testMode ? 'Leave the practice sandbox' : 'Practice without saving anything (leadership)'}
                      </span>
                    </button>
                  )}
                  {driver.isAdmin && (
                    <button style={{...S.item, borderBottom: 'none'}} onClick={() => this.choose('simWatch')}>
                      {driver.simWatch ? 'Stop simulating watcher' : 'Simulate watcher'}
                      <span style={S.sub}>
                        {driver.simWatch ? 'Return to normal (you can drive)' : 'See the read-only watcher view'}
                      </span>
                    </button>
                  )}
                  <div style={S.version}>Version: {APP_VERSION}</div>
                </div>
              </>
            )}
          </div>
          <Nav.Link className="ms-2" style={{fontWeight: 700}} onClick={this.props.userLogout}>Logout</Nav.Link>
        </Nav>
      </Navbar>
    );
  }
}

const S = {
  gear: {font: 'inherit', padding: '6px 14px', border: '2px solid #c7ccd3', borderRadius: 8,
    background: '#fff', cursor: 'pointer', fontWeight: 700},
  take: {font: 'inherit', padding: '6px 14px', borderRadius: 8, border: '2px solid #e0a23a',
    background: '#fff7e8', color: '#9a6b00', fontWeight: 700, fontSize: 13, cursor: 'pointer'},
  takeOff: {opacity: 0.4, cursor: 'not-allowed'},
  overlay: {position: 'fixed', inset: 0, zIndex: 90, background: 'transparent'},
  menu: {position: 'absolute', top: '120%', right: 0, zIndex: 100, minWidth: 270, background: '#fff',
    border: '1px solid #d9dde3', borderRadius: 10, boxShadow: '0 10px 28px rgba(16,24,40,0.18)', overflow: 'hidden'},
  item: {display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none',
    borderBottom: '1px solid #f0f2f5', background: '#fff', cursor: 'pointer', fontWeight: 800, fontSize: 14},
  sub: {display: 'block', fontWeight: 400, fontSize: 12, color: '#6b7280', marginTop: 3},
  version: {padding: '8px 16px', fontSize: 11, color: '#9aa4b2', borderTop: '1px solid #f0f2f5',
    fontVariantNumeric: 'tabular-nums'},
};

export default Navigation;
